import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Calendar, AlertCircle } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";

function publicBookingUrl(hospitalId) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/book/${hospitalId}`;
}

export default function ReservationsTodayPanel() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [now, setNow] = useState(new Date());
  // null = still asking. calendar_reservations defaults to OFF, and this panel
  // used to poll today-visible every 30s regardless: the endpoint answered 403
  // (correctly) and the catch below swallowed it, so a tenant without the module
  // saw a permanently empty reservations card while the box logged thousands of
  // 403s a day. Ask once, then render and poll only if the module is on.
  const [enabled, setEnabled] = useState(null);

  const url = useMemo(() => publicBookingUrl(user?.hospital_id || ""), [user]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/hospital/modules")
      .then(({ data }) => { if (!cancelled) setEnabled(!!data?.modules?.calendar_reservations); })
      .catch(() => { if (!cancelled) setEnabled(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/reservations/today-visible", { params: { minutes_before: 30 } });
        if (!cancelled) setItems(data || []);
      } catch (e) {
        // A 403 here means the module was switched off underneath us; stop polling
        // rather than retry forever.
        if (e?.response?.status === 403) { if (!cancelled) setEnabled(false); return; }
        toast.error(formatErr(e?.response?.data?.detail || e));
      }
    };
    load();
    const t = setInterval(load, 30000);
    const ticker = setInterval(() => setNow(new Date()), 30000);
    return () => { cancelled = true; clearInterval(t); clearInterval(ticker); };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <Card className="p-4 mb-4" data-testid="reservations-today">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-slate-700 font-semibold"><Calendar size={16} /> Today’s reservations</div>
          {items.length === 0 ? (
            <div className="text-sm text-slate-500 mt-2">No upcoming reservations within the next 30 minutes.</div>
          ) : (
            <ol className="mt-3 divide-y divide-slate-100" data-testid="reservations-today-list">
              {items.map((r, i) => {
                const slotDate = new Date(r.reservation_at);
                const isNow = !isNaN(slotDate) && slotDate <= now && i === 0;
                const rowClass = isNow ? "text-red-600 font-bold" : "text-slate-800";
                return (
                  <li key={r.id} className={`py-2 flex items-center gap-3 ${rowClass}`} data-testid={`reservation-row-${i}`}>
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">{i + 1}</span>
                    <span className="font-mono text-xs">{r.reservation_time}</span>
                    <span className="flex-1 truncate">{r.patient_name}</span>
                    {isNow ? <span className="text-[10px] uppercase tracking-wider bg-red-100 text-red-700 border border-red-200 rounded-full px-2 py-0.5">Now</span> : null}
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="shrink-0 text-center">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Booking QR</div>
          <div className="bg-white border border-slate-200 rounded-lg p-2">
            <QRCodeSVG value={url} size={92} />
          </div>
          <div className="text-[10px] text-slate-400 mt-1 max-w-[110px] break-all">{url}</div>
        </div>
      </div>
    </Card>
  );
}
